import React, { useContext, useEffect, useId, useRef, useState } from 'react';
import './BtwnStores.css'
import { Context } from '../../../context/Context';
import { DeleteModal } from '../../modals/Delete';
import { useModalLayer } from '../../modals/modalHooks';
import { translations } from "../../../utils/lang";
import { fetchStoresAPI } from '../../../utils/api/stores';
import { Button, Input, Trash2 } from '../../ui';

const TEXT = {
    ar: {
        removeStore: (name) => `إزالة ${name}`,
        saving: "جارٍ الحفظ...",
        saveFailed: "تعذر حفظ الأذونات. حاول مرة أخرى.",
        search: "بحث عن متجر",
        noStores: "لا توجد متاجر مطابقة.",
        loadingStores: "جارٍ تحميل المتاجر...",
    },
    en: {
        removeStore: (name) => `Remove ${name}`,
        saving: "Saving...",
        saveFailed: "The permissions could not be saved. Please try again.",
        search: "Search stores",
        noStores: "No matching stores.",
        loadingStores: "Loading stores...",
    },
};

// `onUpdate({ between, assignedBranch })` may return a promise; a rejection
// keeps the dialog/selection so nothing looks saved when it wasn't.
export const BtwnStores = ({ between = [], assignedBranch = [], onUpdate }) => {
    const { lang, storeAccess, handleTokenExpiration } = useContext(Context);
    const t = TEXT[lang] || TEXT.en;
    const labels = translations[lang].Users.btwnStores;

    const [selected, setSelected] = useState(between);
    const [branches, setBranches] = useState(assignedBranch);
    const [isSelecting, setIsSelecting] = useState(false);
    const [allStores, setAllStores] = useState([]);
    const [storesLoading, setStoresLoading] = useState(true);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteError, setDeleteError] = useState('');
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const savingRef = useRef(false);
    const dialogRef = useRef(null);
    const titleId = useId();

    const [filterText, setFilterText] = useState('');

    // After a save the parent refetches; show the stored assignment. Compared
    // by value because the default `[]` props are new arrays every render.
    const savedKey = JSON.stringify([between, assignedBranch]);
    useEffect(() => {
        const [savedBetween, savedBranches] = JSON.parse(savedKey);
        setSelected(savedBetween);
        setBranches(savedBranches);
    }, [savedKey]);

    const resetSelection = () => {
        setSelected(between);
        setBranches(assignedBranch);
    };

    const search = filterText.toLowerCase();
    const filteredStores = allStores.filter((store) =>
        String(store.businessName ?? '').toLowerCase().includes(search) ||
        String(store.id ?? '').toLowerCase().includes(search)
    );

    const isUserSelected = (storeId) => selected.some(s => s === storeId);

    const handleCheckboxChange = (id, checked) => {
        if (checked) {
            setSelected(prev => [...prev, id]);
        } else {
            setSelected(prev => prev.filter(s => s !== id));
        }
    };
    const handleBranchChange = (branchName, checked) => {
        if (checked) {
            setBranches(prev => [...prev, branchName]);
        } else {
            setBranches(prev => prev.filter(b => b !== branchName));
        }
    };

    const runUpdate = async (payload) => {
        if (savingRef.current) return false;
        savingRef.current = true;
        setSaving(true);
        try {
            const result = onUpdate ? await onUpdate(payload) : true;
            return result !== false;
        } finally {
            savingRef.current = false;
            setSaving(false);
        }
    };

    const confirmDelete = (id) => {
        setDeleteError('');
        setDeleteTarget(id);
    };

    const handleConfirmedDelete = async (storeId) => {
        const store = allStores.find(s => s.id === storeId);
        const updatedSelected = selected.filter(s => s !== storeId);

        let updatedBranches = branches;
        if (store?.branches?.length > 0) {
            const branchNames = store.branches.map(b => b.name);
            updatedBranches = branches.filter(b => !branchNames.includes(b));
        }

        setDeleteError('');
        try {
            const saved = await runUpdate({
                assignedBranch: updatedBranches,
                between: updatedSelected
            });
            if (!saved) return;
            setSelected(updatedSelected);
            setBranches(updatedBranches);
            setDeleteTarget(null);
        } catch (error) {
            // The dialog stays open with the reason; nothing was removed.
            setDeleteError(error?.message || t.saveFailed);
        }
    };

    const closeSelector = () => {
        if (savingRef.current) return;
        setIsSelecting(false);
        setSaveError('');
        resetSelection();
    };

    const handleSave = async () => {
        setSaveError('');
        try {
            const saved = await runUpdate({ assignedBranch: branches, between: selected });
            if (saved) setIsSelecting(false);
        } catch (error) {
            setSaveError(error?.message || t.saveFailed);
        }
    };

    // Loaded once per panel; `storeAccess`/`handleTokenExpiration` come from
    // Context and change identity on every render.
    const contextRef = useRef({ storeAccess, handleTokenExpiration });
    contextRef.current = { storeAccess, handleTokenExpiration };
    useEffect(() => {
        let active = true;
        const token = localStorage.getItem('auth-token');
        if (!token) {
            contextRef.current.handleTokenExpiration();
            return undefined;
        }
        fetchStoresAPI(token, contextRef.current.storeAccess).then((stores) => {
            if (!active) return;
            if (stores) {
                setAllStores(Array.isArray(stores) ? stores : []);
            } else {
                contextRef.current.handleTokenExpiration();
            }
            setStoresLoading(false);
        }).catch((error) => {
            console.error(error);
            if (active) setStoresLoading(false);
        });
        return () => {
            active = false;
        };
    }, []);

    useModalLayer(isSelecting, { containerRef: dialogRef, onEscape: closeSelector });

    const assignedStores = allStores.filter((store) => selected.includes(store.id));

    return (
        <div className="between-stores-container">
            <h3>{labels.title}</h3>

            <div className="between-stores-table-scroll">
                <table className="between-stores-table-container">
                    <thead>
                        <tr>
                            {labels.header.map((item, i) =>
                                <th className='between-stores-table-header' key={i} scope="col">{item}</th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {selected.length === 0 ? (
                            <tr><td colSpan={labels.header.length}>{labels.noBetween}</td></tr>
                        ) : storesLoading ? (
                            <tr><td colSpan={labels.header.length} role="status">{t.loadingStores}</td></tr>
                        ) : (
                            assignedStores.map((store) => (
                                <tr key={store._id || store.id}>
                                    <td className='between-stores-table-p'>{store.id}</td>
                                    <td className='between-stores-table-p'>{store.businessName}</td>
                                    <td className='between-stores-table-p'>{store.name}</td>
                                    <td className='between-stores-table-p'>{store.phone}</td>
                                    <td>
                                        {store.branches
                                            ?.filter(branch => branches.includes(branch.name))
                                            .map((branch, idx) => (
                                                <p key={branch._id || `${branch.name}-${idx}`}>
                                                    {branch.name}
                                                </p>
                                            ))}
                                    </td>
                                    <td>
                                        <Button
                                            className='between-stores-table-btn-remove'
                                            variant="danger"
                                            appearance="ghost"
                                            size="sm"
                                            rounded
                                            icon={<Trash2 />}
                                            disabled={saving}
                                            aria-label={t.removeStore(store.businessName || store.id)}
                                            title={labels.remove}
                                            onClick={() => confirmDelete(store.id)}
                                        />
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {
                isSelecting ? (
                    <div className="between-stores-select-container">
                        <div
                            ref={dialogRef}
                            className="between-stores-select"
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby={titleId}
                            aria-busy={saving || undefined}
                            tabIndex={-1}
                        >
                            <h3 id={titleId}>{labels.selectTitle}</h3>

                            <Input
                                type="search"
                                placeholder={labels.filterPlaceholder || "Search..."}
                                aria-label={t.search}
                                value={filterText}
                                onChange={(e) => setFilterText(e.target.value)}
                            />

                            <div className="between-store-select-scroll">
                                {storesLoading ? (
                                    <p className="between-stores-note" role="status">{t.loadingStores}</p>
                                ) : filteredStores.length === 0 ? (
                                    <p className="between-stores-note">{t.noStores}</p>
                                ) : filteredStores.map((store) => {
                                    const isSelected = isUserSelected(store.id);

                                    return (
                                        <div key={store._id || store.id} className="between-stores-checkbox-container">
                                            <label className="between-stores-checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={(e) => handleCheckboxChange(store.id, e.target.checked)}
                                                />
                                                <div className="between-stores-checkbox-span">
                                                    <span>{store.id} </span>
                                                    <span>{store.businessName}</span>
                                                </div>
                                            </label>

                                            {/* Branches of a selected store */}
                                            {isSelected && store.branches?.length > 0 && (
                                                <div className="between-stores-checkbox-branch-list">
                                                    {store.branches.map((branch, idx) => {
                                                        const isBranchChecked = branches.includes(branch.name);
                                                        return (
                                                            <label key={branch._id || `${branch.name}-${idx}`} className="between-stores-checkbox-label">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isBranchChecked}
                                                                    onChange={(e) => handleBranchChange(branch.name, e.target.checked)}
                                                                />
                                                                <div className="between-stores-checkbox-span">
                                                                    <span>{branch.name}</span>
                                                                </div>
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {saveError && (
                                <p className="between-stores-error" role="alert">{saveError}</p>
                            )}

                            <div className="add-between-store-btns">
                                <Button fullWidth={true} onClick={handleSave} disabled={saving}>
                                    {saving ? t.saving : labels.save}
                                </Button>
                                <Button
                                    fullWidth={true}
                                    variant="danger"
                                    appearance="soft"
                                    disabled={saving}
                                    onClick={closeSelector}
                                >
                                    {labels.cancel}
                                </Button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <Button onClick={() => setIsSelecting(true)} disabled={saving}>
                        {labels.addBetween}
                    </Button>
                )
            }
            {deleteTarget !== null && (
                <DeleteModal
                    id={deleteTarget}
                    loading={saving}
                    error={deleteError}
                    handleDelete={handleConfirmedDelete}
                    cancel={() => {
                        if (savingRef.current) return;
                        setDeleteTarget(null);
                        setDeleteError('');
                        resetSelection();
                    }}
                />
            )}
        </div >
    );
};
